// Words for a row of the audit log: what its action and its target read as, and the names of the
// organisations and venues it happened in.
//
// **Actions and targets** read as their copy (`audit.actions`, `audit.targetType` in the `hq`
// namespace), and a code with no copy reads as its own words — `shifts.shift.closed` is "Shift
// closed" — so a new action is readable the day it is first written.
//
// **Names** come from what head office can read without writing an audit row of its own: the
// organisations list (`GET /v1/hq/organizations`, spec/14 §3.1: reading the record is not auditing a
// read), fetched only for a role that holds `hq:organizations`. Venues are listed by an audited read
// (spec/14 §3.3), so this page never asks for them: a venue's name shows when the Venues screen has
// already read it this session, and its short id otherwise.

import type { HqOrganizationList, HqVenueList } from '@clavian/api-client';
import { useTranslation } from '@clavian/i18n/react';
import { useInfiniteQuery, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';

import { useApi } from '../../api/client';
import { humanise } from '../../lib/json';
import { usePlatformAccessHere } from '../access';
import { listOrganizationsPage, organizationKeys } from '../organizations/queries';
import { HQ_ORGANIZATIONS } from '../sections';
import { venueKeys } from '../venues/queries';

/** `shifts.shift.closed` → `shifts__shift__closed`: i18next reads a dot as nesting. */
const copyKey = (code: string): string => code.replaceAll('.', '__');

/**
 * A code's own words: `shifts.shift.closed` → "Shift closed" (the module is dropped when the rest
 * names the thing), `sessions.start` → "Sessions start".
 */
export function humaniseAction(action: string): string {
  const segments = action.split('.');
  return humanise((segments.length > 2 ? segments.slice(1) : segments).join(' '));
}

export interface AuditLabels {
  action(code: string): string;
  targetType(type: string): string;
  /** Every action with copy, by its copy lower-cased: what the search box matches. */
  readonly actionsByLabel: ReadonlyMap<string, string>;
}

export function useAuditLabels(): AuditLabels {
  const { t, i18n } = useTranslation('hq');
  return useMemo(() => {
    const copy = (key: string): string | undefined => (i18n.exists(key, { ns: 'hq' }) ? t(key) : undefined);
    const actions = (i18n.getResource(i18n.language, 'hq', 'audit.actions') ?? {}) as Readonly<Record<string, string>>;
    return {
      action: (code) => copy(`audit.actions.${copyKey(code)}`) ?? humaniseAction(code),
      targetType: (type) => copy(`audit.targetType.${type}`) ?? humanise(type),
      actionsByLabel: new Map(Object.entries(actions).map(([key, label]) => [label.toLowerCase(), key.replaceAll('__', '.')])),
    };
  }, [t, i18n]);
}

export interface Directory {
  organization(id: string): string | undefined;
  venue(id: string): string | undefined;
  /** Every organisation named, for the id field's suggestions. */
  readonly organizations: readonly { readonly id: string; readonly name: string }[];
  readonly venues: readonly { readonly id: string; readonly name: string }[];
}

/** The names this page may show without reading anything that is audited. */
export function useDirectory(): Directory {
  const api = useApi();
  const access = usePlatformAccessHere();
  const queryClient = useQueryClient();
  // The same query as the Organisations screen, so a list it already read is used as it is.
  const organizations = useInfiniteQuery({
    queryKey: organizationKeys.list,
    queryFn: ({ pageParam }) => listOrganizationsPage(api, pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => (last.page.hasMore && last.page.cursor !== null ? last.page.cursor : undefined),
    enabled: access.permissions.has(HQ_ORGANIZATIONS),
    retry: false,
  });
  const orgPages = organizations.data as InfiniteData<HqOrganizationList> | undefined;

  // Read from the cache, never asked for: what the Venues screen already read this session.
  const venueKey = JSON.stringify(
    queryClient
      .getQueriesData<InfiniteData<HqVenueList>>({ queryKey: venueKeys.all })
      .flatMap(([, data]) => (data?.pages ?? []).flatMap((page) => page.data.map(({ venue }) => [venue.id, venue.name]))),
  );
  const venueNames = useMemo(() => new Map(JSON.parse(venueKey) as [string, string][]), [venueKey]);

  const orgNames = useMemo(() => {
    const names = new Map<string, string>();
    for (const page of orgPages?.pages ?? []) for (const organization of page.data) names.set(organization.id, organization.name);
    return names;
  }, [orgPages]);

  const organization = useCallback((id: string) => orgNames.get(id), [orgNames]);
  const venue = useCallback((id: string) => venueNames.get(id), [venueNames]);
  return useMemo(
    () => ({
      organization,
      venue,
      organizations: [...orgNames].map(([id, name]) => ({ id, name })),
      venues: [...venueNames].map(([id, name]) => ({ id, name })),
    }),
    [organization, venue, orgNames, venueNames],
  );
}
